import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from 'store';
import { useDCRConfig } from 'common/dcr/utils/useDCRConfig';
import { checkDCRAccess, checkDCRCreateAccess } from './dcrAccess';
import { AuthUserType, RoleVariantEnum } from 'common/graphql/types';

interface DCRAccessResult {
    canAccessDCR: boolean;
    canCreateDCR: boolean;
    isLoading: boolean;
}

export const useDCRAccess = (): DCRAccessResult => {
    const { activeStudySite, activeUser } = useSelector((state: RootState) => state.auth);
    const studyId = activeStudySite?.activeStudy?.studyId;
    const siteId = activeStudySite?.activeSite?.siteId;
    const { dcrConfig, isLoading } = useDCRConfig(studyId, siteId);

    const { canAccessDCR, canCreateDCR } = useMemo(() => {
        if (!activeUser || !dcrConfig || activeUser?.type === AuthUserType.PATIENT) {
            return { canAccessDCR: false, canCreateDCR: false };
        }

        const canAccessDCR = checkDCRAccess({
            userRole: activeUser.roleVClaim?.sk as RoleVariantEnum,
            viewPermissions: dcrConfig.viewRoles ?? [],
            dcrTypeConfigs: dcrConfig.dcrTypeConfigs ?? [],
        });

        const canCreateDCR = checkDCRCreateAccess({
            userRole: activeUser.roleVClaim?.sk as RoleVariantEnum,
            dcrTypeConfigs: dcrConfig.dcrTypeConfigs ?? [],
        });

        return { canAccessDCR, canCreateDCR };
    }, [activeUser, activeUser?.roleVClaim?.sk, dcrConfig?.viewRoles, dcrConfig?.dcrTypeConfigs]);

    return {
        canAccessDCR,
        canCreateDCR,
        isLoading,
    };
};
